import { useEffect, useRef } from 'react';

import { useLocation } from '@tanstack/react-router';

import { useMetadata } from '@dcentralexpedientedigital/fe-cms-api/hooks/use-metadata';
import { getAssetURL } from '@dcentralexpedientedigital/fe-ui/utils/assets';

export function useMetaRenderer() {
    const { pathname } = useLocation();
    const { data } = useMetadata({
        path: pathname,
    });

    const originalTitle = useRef(document.title);

    useEffect(() => {
        document.title = data.title || originalTitle.current;

        const createdTags: HTMLElement[] = [];

        if (data.canonical_url) {
            const canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            canonical.setAttribute('href', data.canonical_url);
            createdTags.push(canonical);
        }

        const robotValue = [
            { value: data.no_index, name: 'noindex' },
            { value: data.no_follow, name: 'nofollow' },
        ]
            .filter(({ value }) => value === true)
            .map(({ name }) => name)
            .join(', ');
        const metas = [
            { property: 'description', content: data.meta_description },
            { property: 'og:title', content: data.og_title || data.title },
            { property: 'og:description', content: data.og_description || data.meta_description },
            { property: 'og:url', content: data.og_url || data.canonical_url },
            { property: 'og:image', content: data.og_image ? getAssetURL(data.og_image) : null },
            {
                property: 'robots',
                content: robotValue ? robotValue : null,
            },
        ];

        createdTags.push(
            ...metas
                .filter(({ content }) => !!content)
                .map(({ property, content }) => {
                    const meta = document.createElement('meta');
                    meta.setAttribute('property', property);
                    meta.setAttribute('content', content ?? '');
                    createdTags.push(meta);
                    return meta;
                }),
        );

        createdTags.forEach((tag) => {
            document.head.appendChild(tag);
        });

        return () => {
            document.title = originalTitle.current;

            createdTags.forEach((tag) => {
                tag.parentNode?.removeChild(tag);
            });
        };
    }, [data]);
}
